'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { Typography, Button, CircularProgress, Tooltip } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { PageHeader } from 'src/components/Settings/components';
import { useAuth } from 'src/context/auth';
import useRoles from 'src/hooks/roles/useRoles';
import { useGetCompanyRolesQuery } from 'src/api/redux';
import { RoleDisplay, RoleActionsDropdown, DeleteRoleDialog } from './components';
import AddEditRolesModal from './components/AddEditRolesModal';
import { ROLE_SECTION_COPY } from './constants';
import { partitionRolesBySource } from './utils';

const RolesAdmin = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const { user } = useAuth();
  const isSupportUser = user?.permissions?.is_support_user;

  const { selectedRole, setSelectedRole, editOrCopyRole, setEditOrCopyRole } =
    useRoles();

  const [addEditRolesModalOpen, setAddEditRolesModalOpen] = useState(false);

  const { data: roles, isLoading } = useGetCompanyRolesQuery(
    { companyId },
    { skip: !companyId }
  );

  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);

  const handleSelectRole = (role: MedScout.Role) => {
    if (!role) return;
    setSelectedRole(role);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelectRole(selectedRole);
    }
  };

  const handleCopyRole = (role: MedScout.Role) => {
    if (!role) return;
    const roleCopy = { ...role, id: undefined };
    setEditOrCopyRole(roleCopy);
    setAddEditRolesModalOpen(true);
  };

  const handleEditRole = (role: MedScout.Role) => {
    if (!role) return;
    setEditOrCopyRole(role);
    setAddEditRolesModalOpen(true);
  };

  const handleCreateRole = () => {
    setEditOrCopyRole(null);
    setAddEditRolesModalOpen(true);
  };

  const handleDeleteRole = (role: MedScout.Role) => {
    setEditOrCopyRole(role);
    setDeleteRoleDialogOpen(true);
  };

  const filteredRoles = useMemo(() => {
    if (!roles?.results) return [];
    return [...roles.results].sort((a, b) => a.label.localeCompare(b.label));
  }, [roles]);

  useEffect(() => {
    if (filteredRoles[0]?.id && !selectedRole) {
      setSelectedRole(filteredRoles[0]);
    }
  }, [filteredRoles, selectedRole, setSelectedRole]);

  const renderRoleRow = (role: MedScout.Role) => {
    const isSelected = selectedRole?.id === role.id;

    return role.id ? (
      <div
        key={role.id}
        role="option"
        tabIndex={0}
        aria-selected={isSelected}
        className={`group flex flex-col pr-2.5
          pl-4 py-4 cursor-pointer transition-all duration-200 hover:bg-neutral-100/50
          border-b border-neutral-100/50 last:border-b-0
          ${
            isSelected
              ? 'bg-sky-50/80 border-r-4 border-r-sky-500 shadow-sm'
              : 'border-r-4 border-r-transparent border-b-0'
          }`}
        onClick={() => handleSelectRole(role)}
        onKeyDown={handleKeyDown}
        data-testid={`roles-admin-role-${role.id}`}
      >
        <div className="flex items-start gap-4 w-full">
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            {/* No per-role marker: the section a role sits under says whether it
                is the company's own or one of MedScout's (MKT-530). */}
            <Typography
              className={`text-sm font-medium truncate ${
                isSelected ? 'text-sky-600' : 'text-neutral-700'
              }`}
            >
              {role.label}
            </Typography>
            <Typography className="text-xs !text-neutral-500 line-clamp-2">
              {role.description}
            </Typography>
          </div>
          <RoleActionsDropdown
            onEdit={() => handleEditRole(role)}
            onCopy={() => handleCopyRole(role)}
            onDelete={() => handleDeleteRole(role)}
            canEdit={!!role.editable}
            canDelete={role.user_count === 0}
            disabled={!isSupportUser}
          />
        </div>
      </div>
    ) : null;
  };

  /**
   * The list, split into MedScout's standard roles and the company's own. The
   * headings only appear once there is a second group to tell apart — a lone
   * "System roles" heading over the whole list would be noise. `role="group"`
   * rather than a bare div so the headings are legal inside the listbox.
   */
  const renderRoleSections = () => {
    const { standardRoles, customRoles, hasCustomRoles } =
      partitionRolesBySource(filteredRoles);

    if (!hasCustomRoles) return filteredRoles.map(renderRoleRow);

    const section = (
      key: string,
      heading: string,
      hint: string | undefined,
      roles: MedScout.Role[]
    ) =>
      roles.length ? (
        <div key={key} role="group" aria-label={heading}>
          <div
            className="sticky top-0 z-10 flex flex-col gap-0.5 border-b
              border-neutral-200 bg-neutral-100/90 px-4 py-2 backdrop-blur-sm"
            data-testid={`roles-admin-section-${key}`}
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
              {heading}
            </span>
            {hint && (
              <span className="text-xs font-normal normal-case text-neutral-500">
                {hint}
              </span>
            )}
          </div>
          {roles.map(renderRoleRow)}
        </div>
      ) : null;

    return [
      section('standard', ROLE_SECTION_COPY.standard, undefined, standardRoles),
      section(
        'custom',
        ROLE_SECTION_COPY.custom,
        ROLE_SECTION_COPY.customHint,
        customRoles
      ),
    ];
  };

  return (
    <div className="relative flex flex-col gap-6 pb-32">
      <PageHeader>
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-neutral-700">
              Roles & Permissions
            </h1>
            <p className="text-sm text-neutral-600">
              Manage roles and permissions for your organization
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              color="neutral"
              startDecorator={<Icon icon={faPlus} />}
              onClick={handleCreateRole}
              className={`rounded-full ${
                !isSupportUser ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="roles-admin-create-role-button"
              disabled={!isSupportUser}
            >
              <Tooltip
                content="Only available to support users"
                position="top"
                showOnCondition={!isSupportUser}
              >
                <span>Create Role</span>
              </Tooltip>
            </Button>
          </div>
        </div>
      </PageHeader>
      <div className="flex flex-col bg-white rounded-lg shadow-sm h-[calc(100vh-25rem)]">
        <div className="flex items-center justify-end">
          <div className="flex flex-col px-2">
            <Typography
              variant="body2"
              className="!text-neutral-500 !text-xs text-right"
            >
              * Roles with assigned users cannot be deleted
            </Typography>
            <Typography
              variant="body2"
              className="!text-neutral-500 !text-xs text-right"
            >
              ** System roles cannot be edited or deleted
            </Typography>
          </div>
        </div>
        <div className="flex flex-1 min-h-0 gap-2">
          <div
            className="flex flex-col w-1/3 min-h-0
            bg-neutral-50/50 border border-neutral-100 overflow-hidden rounded-lg"
          >
            <div
              className="flex flex-col min-h-0 bg-neutral-50/50
              overflow-y-auto rounded-l-xl [&::-webkit-scrollbar]:hidden
              [-ms-overflow-style:none] [scrollbar-width:none]"
              role="listbox"
              aria-label="Roles list"
            >
              {renderRoleSections()}
            </div>
          </div>
          <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden border border-neutral-100 rounded-lg">
            <div className="flex flex-col flex-1 min-h-0 h-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <RoleDisplay role={selectedRole} />
            </div>
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 h-full w-full z-50">
          <div className="absolute inset-0 bg-neutral-50/50 blur-sm" />
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress size="sm" color="neutral" />
          </div>
        </div>
      )}
      <AddEditRolesModal
        isOpen={addEditRolesModalOpen}
        onClose={() => setAddEditRolesModalOpen(false)}
      />
      <DeleteRoleDialog
        open={deleteRoleDialogOpen}
        onClose={() => setDeleteRoleDialogOpen(false)}
        roleId={editOrCopyRole?.id}
      />
    </div>
  );
};

export default RolesAdmin;
